import { ErrorHint } from "src/base-components/ErrorHint";
import { OperatorDropdown } from "src/base-components/OperatorDropdown";
import { CustomDropdownFilter } from "src/base-components/tableColumnFilters/CustomDropdownFilter";
import { SimpleOperatorInput } from "src/base-components/tableColumnFilters/SimpleOperatorInput";
import { useFilter } from "src/base-components/tableColumnFilters/useFilters";
import { Operator } from "src/constants/operators";

const NUMERIC_OPERATORS = [
  {
    key: "eq",
    value: {
      description: "Equal to",
      code: "==",
    },
  },
  {
    key: "gt",
    value: {
      description: "Greater than",
      code: ">",
    },
  },
  {
    key: "lt",
    value: {
      description: "Less than",
      code: "<",
    },
  },
  {
    key: "gte",
    value: {
      description: "Greater than or equal to",
      code: ">=",
    },
  },
  {
    key: "lte",
    value: {
      description: "Less than or equal to",
      code: "<=",
    },
  },
];

export const NumericFilter: React.FC<{
  property: string;
  type: "number" | "integer";
  displayName: string;
  dataLoc?: string;
}> = ({ property, type, displayName, dataLoc }) => {
  const { filters, update, apply, reset, isFiltering, isApplied } = useFilter(
    property,
    [
      {
        key: property,
        operator: "eq",
        values: [],
      },
    ],
  );

  const filter = filters[0];
  const value = filter.values[0];

  return (
    <CustomDropdownFilter
      dataLoc={dataLoc}
      description={`Filter by ${displayName}`}
      isApplied={isApplied()}
      isFiltering={isFiltering({ key: property })}
      operator={filter.operator}
      value={value}
      onApply={apply}
      onResetRequest={() => {
        reset();
      }}
    >
      <SimpleOperatorInput
        prefix={
          <OperatorDropdown
            options={NUMERIC_OPERATORS}
            value={filter.operator as Operator}
            onChange={(value) => {
              update([{ ...filter, operator: value }]);
            }}
          />
        }
        value={value}
        onChange={(value) => update([{ ...filter, values: [value] }])}
      />
      {!isValidNumber(value, type) && (
        <ErrorHint>
          Invalid number: must be{" "}
          {type === "number" ? "a number" : "an integer"}
        </ErrorHint>
      )}
    </CustomDropdownFilter>
  );
};

const isValidNumber = (
  value: string | undefined,
  type: "number" | "integer",
) => {
  if (!value) return true;

  try {
    const parsedValue = JSON.parse(value);

    if (type === "number") {
      return typeof parsedValue === "number";
    }

    return typeof parsedValue === "number" && Number.isInteger(parsedValue);
  } catch {
    return false;
  }
};
